import * as THREE from 'three';

const MAX_BURST_PARTICLES = 80;

interface BurstParticle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  decay: number;
  color: THREE.ColorRepresentation;
}

/** A pool of short-lived, expanding flash particles triggered at brain-surface points. */
export class BurstEffects {
  readonly points: THREE.Points;
  readonly material: THREE.PointsMaterial;

  private readonly geometry: THREE.BufferGeometry;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private particles: BurstParticle[] = [];
  private timeSinceLastBurst = 0;

  constructor(glowTexture: THREE.Texture) {
    this.positions = new Float32Array(MAX_BURST_PARTICLES * 3);
    this.colors = new Float32Array(MAX_BURST_PARTICLES * 3);

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));

    this.material = new THREE.PointsMaterial({
      size: 0.035,
      map: glowTexture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      // Glow through the opaque cortical shell — see neuralActivityParticles.
      depthTest: false,
      transparent: true,
      opacity: 1.0,
      vertexColors: true,
    });

    this.points = new THREE.Points(this.geometry, this.material);
  }

  spawnBurst(x: number, y: number, z: number, color: THREE.ColorRepresentation, count = 15): void {
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= MAX_BURST_PARTICLES) break;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 0.01 + Math.random() * 0.06;
      this.particles.push({
        x,
        y,
        z,
        vx: Math.sin(phi) * Math.cos(theta) * speed,
        vy: Math.sin(phi) * Math.sin(theta) * speed,
        vz: Math.cos(phi) * speed,
        life: 1.0,
        decay: 0.015 + Math.random() * 0.04,
        color,
      });
    }
  }

  /**
   * Advances existing bursts and, at a rate of burstsPerSecond, spawns a new
   * one at a random point on the given surface geometry with the given color.
   */
  update(
    deltaSeconds: number,
    burstsPerSecond: number,
    surfaceGeometry: THREE.BufferGeometry,
    color: THREE.ColorRepresentation,
    intensity: number,
  ): void {
    this.timeSinceLastBurst += deltaSeconds;
    const interval = 1 / Math.max(burstsPerSecond, 0.005);
    if (this.timeSinceLastBurst > interval) {
      this.timeSinceLastBurst = 0;
      const surfacePositions = surfaceGeometry.attributes.position;
      const vi = Math.floor(Math.random() * surfacePositions.count);
      const sx = surfacePositions.getX(vi);
      const sy = surfacePositions.getY(vi);
      const sz = surfacePositions.getZ(vi);
      const sr = Math.sqrt(sx * sx + sy * sy + sz * sz);
      const outwardScale = (sr + 0.03) / Math.max(sr, 0.001);
      const count = Math.floor(5 + intensity * 8);
      this.spawnBurst(sx * outwardScale, sy * outwardScale, sz * outwardScale, color, count);
    }

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.z += p.vz;
      p.life -= p.decay;
      if (p.life <= 0) this.particles.splice(i, 1);
    }

    for (let i = 0; i < MAX_BURST_PARTICLES; i++) {
      if (i < this.particles.length) {
        const p = this.particles[i];
        this.positions[i * 3] = p.x;
        this.positions[i * 3 + 1] = p.y;
        this.positions[i * 3 + 2] = p.z;
        const c = new THREE.Color(p.color);
        this.colors[i * 3] = c.r * p.life;
        this.colors[i * 3 + 1] = c.g * p.life;
        this.colors[i * 3 + 2] = c.b * p.life;
      } else {
        this.positions[i * 3 + 1] = -99;
        this.colors[i * 3] = 0;
        this.colors[i * 3 + 1] = 0;
        this.colors[i * 3 + 2] = 0;
      }
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;
    this.material.opacity = this.particles.length > 0 ? 0.9 : 0;
  }
}
